// One cold point: chart (6 h / 24 h / 7 d / custom), its excursions, and its settings (admin/manager).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Thermometer } from 'lucide-react';
import { coldChain } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import EmptyState from '@/components/shared/EmptyState';
import { orgTimezone, dateTimeInTz } from '@/features/staff/attendanceModel';
import ColdPointChart from './ColdPointChart';
import ColdPointForm from './ColdPointForm';
import ColdPointHistoryTab from './ColdPointHistoryTab';
import { formatRange, formatTemp } from './coldChainModel';
import { endReasonLabel } from './coldChainReportModel';

const WINDOWS = { h6: 6 * 3_600_000, h24: 24 * 3_600_000, d7: 7 * 86_400_000 };
const pill = (on) => `rounded-xl px-3 py-1.5 text-xs font-semibold ${on ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`;
const dateInput = 'ms-2 rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700 dark:bg-slate-800';

export default function ColdPointDrawer({ pointId, canManage, onClose, onSaved }) {
  const { t } = useTranslation();
  const tz = orgTimezone(useSelector((s) => s.org?.settings));
  const [tab, setTab] = useState('chart');
  const [win, setWin] = useState('h24');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [history, setHistory] = useState(null);
  const [missing, setMissing] = useState(false);          // 404: gone, or not visible to this user
  const [config, setConfig] = useState(null);
  const [configState, setConfigState] = useState('idle'); // idle | loading | ready | none
  const [saves, setSaves] = useState(0);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);   // { code, fields? } from the last failed save
  const loadSeq = useRef(0);
  const deviceId = history?.point?.deviceId ?? null;

  const range = useMemo(() => {
    if (win === 'custom') {
      const from = Date.parse(custom.from);
      const to = Date.parse(custom.to);
      return Number.isFinite(from) && Number.isFinite(to) && from < to
        ? { from: new Date(from).toISOString(), to: new Date(to).toISOString() }
        : null;
    }
    const now = Date.now();
    return { from: new Date(now - WINDOWS[win]).toISOString(), to: new Date(now).toISOString() };
  }, [win, custom]);

  const load = useCallback(async () => {
    if (!range) return;
    const seq = ++loadSeq.current;
    try {
      const h = await coldChain.history(pointId, range);
      if (seq !== loadSeq.current) return;   // a newer window was asked for: drop this reply
      setMissing(false);
      setHistory(h);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      if (e?.status === 404) { setMissing(true); setHistory(null); return; }
      showToast(e?.message || t('coldChain.loadError', 'Could not load cold chain data'));
    }
  }, [pointId, range, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'settings' || !deviceId) return undefined;
    let cancelled = false;
    setConfigState('loading');
    coldChain.devicePoints(deviceId)
      .then((r) => {
        if (cancelled) return;
        const found = (r?.points ?? []).find((p) => p.id === pointId) ?? null;   // an archived point is not listed
        setConfig(found);
        setConfigState(found ? 'ready' : 'none');
      })
      .catch(() => { if (!cancelled) { setConfig(null); setConfigState('none'); } });
    return () => { cancelled = true; };
  }, [tab, deviceId, pointId, saves]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(payload) {
    setBusy(true);
    setServerError(null);
    try {
      await coldChain.updatePoint(pointId, payload);
      showToast(t('coldChain.setup.saved', 'Cold point saved'));
      setSaves((n) => n + 1);
      onSaved?.();
      await load();
    } catch (e) {
      if (e?.status === 409) {
        // Someone saved first: drop the stale form and reload the row we now have to edit.
        showToast(t('coldChain.errors.versionConflict', 'Someone else changed this cold point — reloaded the latest settings'));
        setServerError({ code: 'version_conflict' });
        setSaves((n) => n + 1);
      } else if (e?.code === 'reason_required') {
        setServerError({ code: 'reason_required', fields: e.body?.fields ?? [] });
      } else {
        showToast(e?.message || t('coldChain.errors.save', 'Could not save'));
      }
    } finally {
      setBusy(false);
    }
  }

  const windowLabel = { h6: t('coldChain.drawer.h6', '6 h'), h24: t('coldChain.drawer.h24', '24 h'), d7: t('coldChain.drawer.d7', '7 days'), custom: t('coldChain.drawer.custom', 'Custom') };
  const tabs = [
    ['chart', t('coldChain.drawer.chart', 'Chart')],
    ['excursions', t('coldChain.drawer.excursions', 'Excursions')],
    ['history', t('coldChain.drawer.history', 'History')],
    ...(canManage ? [['settings', t('coldChain.drawer.settings', 'Settings')]] : []),
  ];
  const excursions = [...(history?.excursions ?? [])].reverse();   // newest first

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
      <aside role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full max-w-[760px] overflow-y-auto border-s border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3 dark:border-slate-800 dark:bg-slate-900">
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100"><bdi>{history?.point?.name ?? '…'}</bdi></h3>
            {history?.point && <p className="text-xs text-slate-500"><bdi>{formatRange(history.point.rangeMin, history.point.rangeMax)}</bdi></p>}
          </div>
          <button type="button" onClick={onClose} aria-label={t('coldChain.drawer.close', 'Close')} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16} /></button>
        </header>
        {missing ? (
          <div className="p-5">
            <EmptyState icon={Thermometer} title={t('coldChain.drawer.notFound', 'This cold point is not available')} subtitle={t('coldChain.drawer.notFoundHint', 'It may have been removed, or it is outside your access.')} />
          </div>
        ) : (<>
        <nav className="flex gap-4 border-b border-slate-200 px-5 dark:border-slate-800">
          {tabs.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-1 py-2.5 text-xs font-semibold ${tab === key ? 'border-slate-900 text-slate-900 dark:border-slate-100 dark:text-slate-100' : 'border-transparent text-slate-500'}`}>
              {label}
            </button>
          ))}
        </nav>
        <div className="space-y-4 p-5">
          {tab === 'chart' && (
            <>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(windowLabel).map((w) => (
                  <button key={w} type="button" aria-pressed={win === w} className={pill(win === w)} onClick={() => setWin(w)}>{windowLabel[w]}</button>
                ))}
              </div>
              {win === 'custom' && (
                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                  <label>{t('coldChain.drawer.from', 'From')}<input type="datetime-local" className={dateInput} value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} /></label>
                  <label>{t('coldChain.drawer.to', 'To')}<input type="datetime-local" className={dateInput} value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} /></label>
                </div>
              )}
              {win === 'custom' && !range && <p className="text-xs text-slate-500">{t('coldChain.drawer.pickRange', 'Pick a start and an end to draw the chart.')}</p>}
              {history ? <ColdPointChart history={history} tz={tz} /> : range && <Loader2 size={16} className="animate-spin text-slate-400" />}
              <ul className="flex flex-wrap gap-3 text-[11px] text-slate-500">
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-emerald-200" />{t('coldChain.drawer.legendRange', 'Safe range')}</li>
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-slate-300" />{t('coldChain.drawer.legendGap', 'No data')}</li>
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded border border-slate-300 bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_1px,transparent_1px,transparent_3px)]" />{t('coldChain.drawer.legendOff', 'Monitoring off')}</li>
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-rose-200" />{t('coldChain.kind.outOfRange', 'Out of range')}</li>
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-0.5 bg-amber-500" />{t('coldChain.drawer.legendDoor', 'Door opened')}</li>
                <li className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-blue-400" />{t('coldChain.drawer.legendMoving', 'Moving')}</li>
              </ul>
            </>
          )}
          {tab === 'excursions' && (
            excursions.length ? (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {excursions.map((e) => (
                  <li key={e.id} className="py-2 text-sm">
                    <span className={`me-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.kind === 'no_data' ? 'bg-slate-100 text-slate-600' : 'bg-rose-50 text-rose-700'}`}>
                      {e.kind === 'no_data' ? t('coldChain.kind.noData', 'No data') : t('coldChain.kind.outOfRange', 'Out of range')}
                    </span>
                    <bdi>{dateTimeInTz(e.startedAt, tz)}</bdi> → {e.endedAt ? <><bdi>{dateTimeInTz(e.endedAt, tz)}</bdi>{endReasonLabel(e.endedReason, t) && <span className="ms-2 text-slate-500">· {endReasonLabel(e.endedReason, t)}</span>}</> : t('coldChain.ongoing', 'Ongoing')}
                    {e.assumed && <span className="ms-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{t('coldChain.excursion.assumed', 'assumed')}</span>}
                    {typeof e.peakValue === 'number' && <span className="ms-2 text-slate-500">{t('coldChain.peak', 'Peak')} <bdi>{formatTemp(e.peakValue)}</bdi></span>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500">{history ? t('coldChain.drawer.noExcursions', 'No excursions in this period') : '…'}</p>
          )}
          {tab === 'history' && <ColdPointHistoryTab key={`${pointId}-${saves}`} pointId={pointId} tz={tz} />}
          {tab === 'settings' && canManage && (
            configState === 'ready' && config
              ? <ColdPointForm key={`${pointId}-${saves}`} initial={config} busy={busy} onSubmit={save} tz={tz} serverError={serverError} />
              : configState === 'none'
                ? <p className="text-sm text-slate-500">{t('coldChain.drawer.archivedNoSettings', 'This cold point is archived, so its settings cannot be changed.')}</p>
                : <Loader2 size={16} className="animate-spin text-slate-400" />
          )}
        </div>
        </>)}
      </aside>
    </div>
  );
}
